import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, TagIcon, PercentIcon, DollarSignIcon } from 'lucide-react';
import { CampaignList } from '../components/campaigns/CampaignList';
import { Campaign } from '../types/Campaign';
// Mock campaign data based on screenshots
const mockCampaigns: Campaign[] = [{
  id: 'RBLSHOP2025',
  title: '[RLife] 7-days Promotion for Shop',
  description: '[RLife] 7-days Promotion for Shop',
  expiryDate: '07/10/2025',
  discountPercentage: 0,
  fixedDiscountAmount: 50,
  currency: 'THB',
  discountCarriedBy: 'Merchant',
  merchantPercentage: 0,
  budget: 5000,
  budgetUsed: 0,
  strictlyEnforceBudget: true,
  validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
  code: 'RBLSHOP2025',
  category: 'Discount code',
  owner: 'Merchant',
  codeStrategy: 'Global',
  minTransactionValue: 0,
  maxTransactionValue: 999999,
  minDiscount: 50,
  maxDiscount: 50,
  status: 'active',
  companies: ['Rabbit Life'],
  transactionVolume: 47250,
  transactionCount: 63
}, {
  id: 'BBL10THB',
  title: 'BBL Special Package (THB 10 Discount)',
  description: 'DISCOUNT_COUPON',
  expiryDate: '02/08/2025',
  discountPercentage: 0,
  fixedDiscountAmount: 10,
  currency: 'THB',
  discountCarriedBy: 'Merchant',
  merchantPercentage: 0,
  budget: 11000,
  budgetUsed: 10960,
  strictlyEnforceBudget: true,
  validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
  code: 'BBL10THB',
  category: 'Discount code',
  owner: 'Merchant',
  codeStrategy: 'Global',
  minTransactionValue: 0,
  maxTransactionValue: 999999,
  minDiscount: 10,
  maxDiscount: 10,
  status: 'active',
  transactionVolume: 1284500,
  transactionCount: 1096
}, {
  id: 'BULK2025',
  title: 'Summer Festival Bulk Codes',
  description: 'Unique codes for summer festival attendees',
  startDate: '01/06/2025',
  expiryDate: '31/08/2025',
  discountPercentage: 15,
  fixedDiscountAmount: 0,
  currency: 'THB',
  discountCarriedBy: 'Merchant',
  merchantPercentage: 0,
  budgetType: 'fixed',
  budget: 50000,
  budgetUsed: 12500,
  strictlyEnforceBudget: true,
  paymentMethods: ['Card', 'PromptPay'],
  cardTargetingMode: 'all',
  cardBrands: [],
  cardBin: '',
  cardIssuingCountry: '',
  cardType: 'All Types',
  validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
  code: '',
  category: 'Discount code',
  owner: 'Merchant',
  codeStrategy: 'Bulk',
  minTransactionValue: 500,
  maxTransactionValue: 999999,
  minDiscount: 0,
  maxDiscount: 1000,
  status: 'active',
  transactionVolume: 312750,
  transactionCount: 245
}];
export function Campaigns() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const handleCreateCampaign = () => {
    navigate('/campaign-form');
  };
  const handleCampaignClick = (campaignId: string) => {
    navigate(`/campaign-details?id=${campaignId}`);
  };
  const filteredCampaigns = mockCampaigns.filter(campaign => {
    const matchesSearch = campaign.title.toLowerCase().includes(searchTerm.toLowerCase()) || campaign.id.toLowerCase().includes(searchTerm.toLowerCase()) || campaign.code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || campaign.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  // Calculate summary metrics
  const activeCampaigns = mockCampaigns.filter(c => c.status === 'active');
  const totalBudget = activeCampaigns.reduce((sum, c) => sum + c.budget, 0);
  const totalUsed = activeCampaigns.reduce((sum, c) => sum + c.budgetUsed, 0);
  return <div className="space-y-6 max-w-7xl mx-auto pb-12" data-id="element-6631">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-6632">
        <div data-id="element-6633">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-6634">Campaigns</h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-6635">
            Manage your discount codes and promotional campaigns
          </p>
        </div>
        <button onClick={handleCreateCampaign} className="px-4 py-2 bg-[#F7941D] hover:bg-[#D97B06] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-6636">
          Create Campaign
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-6637">
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-6638">
          <div className="flex items-center gap-3 mb-2" data-id="element-6639">
            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg" data-id="element-6640">
              <TagIcon size={20} data-id="element-6641" />
            </div>
            <div className="text-sm font-medium text-gray-500" data-id="element-6642">
              Active Campaigns
            </div>
          </div>
          <div className="text-2xl font-semibold text-gray-900" data-id="element-6643">
            {activeCampaigns.length}
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-6644">
          <div className="flex items-center gap-3 mb-2" data-id="element-6645">
            <div className="p-2 bg-green-50 text-green-600 rounded-lg" data-id="element-6646">
              <PercentIcon size={20} data-id="element-6647" />
            </div>
            <div className="text-sm font-medium text-gray-500" data-id="element-6648">
              Total Redemptions
            </div>
          </div>
          <div className="text-2xl font-semibold text-gray-900" data-id="element-6649">1,248</div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-6650">
          <div className="flex items-center gap-3 mb-2" data-id="element-6651">
            <div className="p-2 bg-purple-50 text-purple-600 rounded-lg" data-id="element-6652">
              <DollarSignIcon size={20} data-id="element-6653" />
            </div>
            <div className="text-sm font-medium text-gray-500" data-id="element-6654">
              Budget Allocated
            </div>
          </div>
          <div className="text-2xl font-semibold text-gray-900" data-id="element-6655">
            THB {totalBudget.toLocaleString()}
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-6656">
          <div className="flex items-center gap-3 mb-2" data-id="element-6657">
            <div className="p-2 bg-orange-50 text-orange-600 rounded-lg" data-id="element-6658">
              <DollarSignIcon size={20} data-id="element-6659" />
            </div>
            <div className="text-sm font-medium text-gray-500" data-id="element-6660">Budget Used</div>
          </div>
          <div className="text-2xl font-semibold text-gray-900" data-id="element-6661">
            THB {totalUsed.toLocaleString()}
          </div>
          <div className="w-full bg-gray-100 rounded-full h-1.5 mt-3 overflow-hidden" data-id="element-6662">
            <div className="bg-orange-500 h-1.5 rounded-full" style={{
            width: `${totalUsed / totalBudget * 100}%`
          }} data-id="element-6663"></div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex flex-col sm:flex-row gap-4 justify-between items-center" data-id="element-6664">
        <div className="relative w-full sm:w-96" data-id="element-6665">
          <SearchIcon size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-6666" />
          <input type="text" placeholder="Search campaigns by name, ID, or code..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] focus:outline-none transition-colors" data-id="element-6667" />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto" data-id="element-6668">
          <span className="text-sm text-gray-500 font-medium" data-id="element-6669">Status:</span>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border border-gray-200 rounded-lg text-sm px-3 py-2 focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] focus:outline-none transition-colors" data-id="element-6670">
            <option value="all" data-id="element-6671">All Statuses</option>
            <option value="active" data-id="element-6672">Active</option>
            <option value="inactive" data-id="element-6673">Inactive</option>
            <option value="expired" data-id="element-6674">Expired</option>
          </select>
        </div>
      </div>

      {/* Campaign List */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-6675">
        <CampaignList campaigns={filteredCampaigns} onCampaignClick={handleCampaignClick} data-id="element-6676" />
      </div>
    </div>;
}